<template>
  <div class="space-y-16 lg:space-y-24 py-12">
    <TContainer>
      <div class="flex items-start justify-between gap-4">
        <div>
          <THeading
            :level="3"
            class="mb-2"
          >
            {{ t('title', { name: data?.campground?.Name, placeName: data?.campground?.PlaceName }) }}
          </THeading>
          <p class="font-semibold">
            {{ t('subtitle') }}
          </p>
        </div>

        <TMenu v-if="userCanManageCampgrounds && data?.campground?.Id">
          <template #icon>
            <TIcon :icon="['far', 'ellipsis-vertical']" />
          </template>
          <div>
            <TMenuItem
              :text="t('editCampground')"
              icon="pencil"
              class="px-4"
              :to="localePath({ name: ROUTE_BASE_NAME.rv_rentals_campground_edit, params: { campgroundSlug: data.campground.Id } })"
            />
          </div>
        </TMenu>
      </div>

      <AppMosaicImages
        v-if="campgroundMosaicImages.length"
        :images="campgroundMosaicImages"
        aspect-ratio="21/9"
        class="mt-6"
      />
      <TBreadcrumb
        v-if="breadcrumbs?.length"
        :items="breadcrumbs"
        class="mt-4 lg:justify-end"
      />
    </TContainer>

    <TContainer size="xl">
      <RvRentalsHostAd
        :country-code="(data?.campground?.CountryCode as COUNTRY_SHORTS)"
        :max-rv-earnings="rvEarnings?.max"
      />
    </TContainer>

    <TContainer
      size="xl"
      class="space-y-8"
    >
      <div class="grid sm:grid-cols-[2fr_1fr] gap-x-12 gap-y-8">
        <div class="space-y-4">
          <div class="flex items-center justify-between gap-2">
            <THeading
              :level="5"
              tag="h2"
            >
              {{ t('campgroundInPlace', { placeName: data?.campground?.PlaceName ?? '' }) }}
            </THeading>

            <CampgroundsAveragePrice :average-price="averagePriceText" />
          </div>

          <AppCollapsibleContent
            v-if="data?.campground?.Description"
            :content="data.campground.Description"
            more-button-variant="link"
            is-show-more-button
            :line-clamp="10"
          />

          <div class="border-t border-gray-200 pt-4">
            <p class="font-semibold">
              {{ t('contact') }}
            </p>

            <div class="flex flex-wrap gap-x-6 gap-y-4 mt-2">
              <a
                v-if="data?.campground?.PhoneNumber"
                v-track-external-link="{ cta: 'campground-phone' }"
                :href="`tel:${data.campground.PhoneNumber}`"
                class="inline-flex items-center gap-1 hover:underline text-sm"
              >
                <TIcon
                  :icon="['fal', 'phone']"
                  fixed-width
                />
                {{ t('phone') }}
              </a>

              <NuxtLink
                v-if="data?.campground?.WebsiteLink"
                v-track-external-link="{ cta: 'campground-website' }"
                :to="data.campground.WebsiteLink"
                external
                target="_blank"
                rel="nofollow noopener noreferrer"
                class="inline-flex items-center gap-1 hover:underline text-sm"
              >
                <TIcon
                  :icon="['fal', 'globe']"
                  fixed-width
                />
                {{ t('website') }}
              </NuxtLink>

              <NuxtLink
                v-if="data?.campground?.BookingLink"
                v-track-external-link="{ cta: 'campground-book-campsite' }"
                :to="data.campground.BookingLink"
                external
                target="_blank"
                rel="noopener noreferrer"
                class="inline-flex items-center gap-1 hover:underline text-sm"
              >
                <TIconCustom
                  v-if="data?.campground.BookingProvider === 'Campspot'"
                  name="campspot-pin"
                  size="xl"
                  fixed-width
                />
                <TIcon
                  v-else
                  :icon="['fal', 'calendar']"
                  fixed-width
                />
                {{ t('bookCampsite') }}
              </NuxtLink>
            </div>
          </div>
        </div>
        <div>
          <p class="mb-4 text-lg font-semibold">
            {{ t('campgroundFeatures') }}
          </p>
          <ul
            v-if="displayAmenities.length"
            class="space-y-4"
          >
            <li
              v-for="amenity in displayAmenities"
              :key="amenity.key"
              class="flex items-center gap-3"
            >
              <TIcon
                v-if="amenity.icon"
                :icon="amenity.icon"
                size="xl"
                fixed-width
              />
              <span class="font-medium">
                {{ amenity.label }}
              </span>
            </li>
          </ul>
        </div>
      </div>

      <div class="mt-8 grid sm:grid-cols-2 gap-4">
        <div class="bg-gray-50 py-4 px-6 rounded-lg">
          <div class="font-medium mb-2">
            {{ t('hookups') }}
          </div>

          <ul class="space-y-2">
            <li class="flex items-center gap-x-2">
              <TIcon
                :icon="['fal', hasAmenity('HasEletricalHookup') || electricHookupsTypes.length ? 'check' : 'xmark']"
                fixed-width
              />
              <div>
                {{ t('campgroundAmenities.electricHookup') }}
                <span
                  v-if="electricHookupsTypes.length"
                  class="text-xs text-gray-500"
                >
                  {{ electricHookupsTypes }}
                </span>
              </div>
            </li>
            <li class="flex items-center gap-x-2">
              <TIcon
                :icon="['fal', hasAmenity('HasWaterHookup') ? 'check' : 'xmark']"
                fixed-width
              />
              {{ t('campgroundAmenities.waterHookup') }}
            </li>
            <li class="flex items-center gap-x-2">
              <TIcon
                :icon="['fal', hasAmenity('HasSewerHookup') ? 'check' : 'xmark']"
                fixed-width
              />
              {{ t('campgroundAmenities.sewerHookup') }}
            </li>
          </ul>
        </div>

        <div class="bg-gray-50  py-4 px-6 rounded-lg">
          <div class="font-medium mb-2">
            {{ t('rvSitesCount', data?.campground?.NumberOfSites ?? 0) }}
          </div>

          <ul class="space-y-2">
            <li>
              <TIcon
                :icon="['fal', hasAmenity('HasPullThrusSitesAvailable') ? 'check' : 'xmark']"
                fixed-width
              />
              {{ t('campgroundAmenities.pullThroughSites') }}
            </li>

            <li>
              <TIcon
                :icon="['fal', hasAmenity('IsBigRigFriendly') ? 'check' : 'xmark']"
                fixed-width
              />
              {{ t('campgroundAmenities.bigRigs') }}
            </li>
            <li>
              <TIcon
                :icon="['fal', hasAmenity('HasTentsSites') ? 'check' : 'xmark']"
                fixed-width
              />
              {{ t('campgroundAmenities.tentSites') }}
            </li>
          </ul>
        </div>
      </div>

      <div>
        <div class="font-medium mb-2">
          {{ t('campgroundRestrictions') }}
        </div>

        <ul class="space-y-2">
          <li>
            <TIcon
              :icon="isOpenYearRound ? ['fal', 'check'] : ['fal', 'triangle-exclamation']"
              fixed-width
            />
            {{ seasonText }}
          </li>

          <li v-if="data?.campground?.MaxRVLength">
            <TIcon
              :icon="['fal', 'triangle-exclamation']"
              fixed-width
            />
            {{ t('maxRVLength', { length: data?.campground?.MaxRVLength }) }}
          </li>
          <li v-else>
            <TIcon
              :icon="['fal', 'check']"
              fixed-width
            />
            {{ t('noMaxRVLength') }}
          </li>

          <li
            v-if="hasAmenity('IsMotorizedOnly')"
          >
            <TIcon
              :icon="['fal', 'triangle-exclamation']"
              fixed-width
            />
            {{ t('motorizedRVsOnly') }}
          </li>
          <li v-else>
            <TIcon
              :icon="['fal', 'check']"
              fixed-width
            />
            {{ t('allRVsWelcome') }}
          </li>

          <li v-if="hasAmenity('HasAgeRestriction')">
            <TIcon
              :icon="['fal', 'triangle-exclamation']"
              fixed-width
            />
            {{ t('ageRestriction') }}
          </li>
          <li v-else>
            <TIcon
              :icon="['fal', 'check']"
              fixed-width
            />
            {{ t('noAgeRestrictions') }}
          </li>
        </ul>
      </div>
    </TContainer>

    <TContainer
      v-if="gridRvs?.length"
      size="xl"
      class="space-y-8"
    >
      <THeading
        tag="h2"
        :level="2"
      >
        {{ t('rentalRVsNear', { name: data?.campground?.Name ?? '' }) }}
      </THeading>

      <Grid
        :max-results="gridItemCount"
        :sm-cols="2"
        :lg-cols="3"
        :rows="6"
        :sm-rows="4"
        :lg-rows="3"
      >
        <template #default="{ getItemClass }">
          <template
            v-for="(rv, index) in gridRvs"
            :key="rv.Id"
          >
            <CardRv
              :index="index"
              search-request-id=""
              :rv="rv"
              no-lazy-load
              cta="poi-rvs-grid"
              discovery-source="POI"
              :class="getItemClass(rvGridIndex(index))"
              is-seo-variant
              @visible:rv="queueViewableImpression"
            />
            <CardAd
              v-if="index === adPosition"
              :image="{ src: getStaticImage({ path: 'campgrounds/ad/classb.webp', preset: presets.LANDSCAPE_SM }) ?? '', alt: '' }"
              :title="listAnRvAdTitle"
              :to="linkListRv"
              :cta="t('ads.listAnRV.cta')"
              :class="getItemClass(adPosition + 1)"
            />
          </template>
        </template>
      </Grid>

      <div class="flex justify-center">
        <TButton
          variant="highlight"
          class="w-full sm:w-auto"
          @click="navigateToRvSearch"
        >
          {{ t('exploreRVRentals', { locationName: data?.campground?.City ? t('near', { location: data?.campground?.PlaceName }) : '' }) }}
        </TButton>
      </div>
    </TContainer>

    <ExperimentComponent experiment-key="rvz26351UpdatedBrandDifferentiatorCards">
      <template #enabled>
        <TContainer
          size="xl"
          class="mb-8"
        >
          <GeneralBrandPillars focus="guest" />
        </TContainer>
      </template>
    </ExperimentComponent>

    <LazyRvRentalsBestReviews
      v-if="data?.tripReviews?.length"
      :rv-title="t('rvs')"
      :location-title="t('campgroundTitle')"
      :location-name="data?.campground?.Name ?? ''"
      :reviews="data?.tripReviews ?? []"
    />

    <div v-if="data?.campground?.Latitude && data?.campground?.Longitude">
      <TContainer
        size="xl"
        class="mb-8 mt-24"
      >
        <THeading
          :level="3"
          tag="h2"
          class="mb-1"
        >
          {{ t('rvRentalsNear', { name: data?.campground?.Name ?? '' }) }}
        </THeading>
      </TContainer>

      <TContainer>
        <RvRentalsMap
          :center="{
            lat: data?.campground?.Latitude,
            lng: data?.campground?.Longitude,
          }"
          :zoom="9"
          :rvs="data?.rvs"
          :campgrounds="[data?.campground]"
        />

        <template v-if="data?.campground?.FullCompactAddress">
          <p class="mt-4 text-center font-medium">
            {{ data.campground.FullCompactAddress }}
          </p>

          <p class="text-center">
            <NuxtLink
              :to="`https://www.google.com/maps/dir//${encodeURIComponent(data.campground.FullCompactAddress)}`"
              external
              target="_blank"
              class="underline hover:no-underline"
              rel="nofollow noopener noreferrer"
            >
              {{ t('getDirections') }}
            </NuxtLink>
          </p>
        </template>
      </TContainer>
    </div>

    <TContainer
      v-if="data?.parks?.length"
      size="xl"
      class="my-24"
    >
      <RvRentalsNearbyParks
        :title="t('ParksToVisitNear', { name: data?.campground?.Name ?? '' })"
        :parks="data?.parks"
      />
    </TContainer>

    <TContainer size="xl">
      <RvRentalsCampgroundDelivery class="my-16" />

      <div class="flex justify-center">
        <TButton
          variant="highlight"
          class=""
          @click="navigateToRvSearch({ Delivery: true })"
        >
          {{ t('viewDeliverableRVs') }}
        </TButton>
      </div>
    </TContainer>
  </div>
</template>

<script setup lang="ts">
import { AMENITY_ICONS, AMENITY_LABELS } from '~/constants/campground'
import { POINT_OF_INTEREST_COUNTRY_ID, POINT_OF_INTEREST_TYPE } from '~/constants/point-of-interest'

import type { Breadcrumb } from '~/components/_tailwind/breadcrumb/types'
import { type COUNTRY_SHORTS, ROUTE_BASE_NAME } from '~/constants'
import type { CampgroundAmenity } from '~/types/campground'

definePageMeta({
  layout: 'static',

  i18n: {
    paths: {
      en: '/rv-rentals/campground/[campgroundSlug]',
      fr: '/locations-vr/camping/[campgroundSlug]',
    },
  },
})

const route = useRoute()
const { locale, t, te } = useI18n()
const localePath = useLocalePath()
const { $rentals, $gis } = useNuxtApp()
const { userCanManageCampgrounds } = useUser()
const { linkListRv } = useOwnerCommon()
const { priceDisplay } = usePrice()
const { getStaticImage, getDynamicImage, presets } = useImage()
const { navigateToRvSearch } = useSearch()
const searchData = useSearchData()
const { getPOIs } = usePointOfInterest()

const campgroundSlug = computed(() => Array.isArray(route.params.campgroundSlug) ? route.params.campgroundSlug.join(' ') : route.params.campgroundSlug)

const { data } = await useAsyncData(`rv-rentals-campground-${campgroundSlug.value}-${locale.value}`, async () => {
  if (!campgroundSlug.value) {
    return {
      campground: undefined,
    }
  }

  const campground = await $rentals('/api/campgrounds/{slug}', {
    path: {
      slug: campgroundSlug.value,
    },
    query: {
      language: lookupLocaleToLanguage(locale.value),
    },
  })

  if (!campground) {
    return { campground }
  }

  const countryCode = campground?.CountryCode as COUNTRY_SHORTS

  const [gisData, nearbyLocations, nearbyRVs, tripReviews, nearbyParks] = await Promise.all([
    $gis('/Geocoding/Geocode', {
      query: {
        Address: campground.FullCompactAddress
          ?? [campground?.Name, campground?.Region, campground?.CountryCode].filter(Boolean).join(', '),
      },
    }),

    getPOIs({
      Latitude: campground?.Latitude,
      Longitude: campground?.Longitude,
      PageSize: 12,
      PointOfInterestTypeName: 'City',
      DistanceKM: 250,
      ParentPointOfInterestId: POINT_OF_INTEREST_COUNTRY_ID[campground?.CountryCode as COUNTRY_SHORTS],
    }),

    $rentals('/api/rvlistings/unified-search', {
      query: {
        SearchLat: campground?.Latitude,
        SearchLng: campground?.Longitude,
        Country: countryCode,
        PageSize: 50,
        SearchDistance: 200,
        IncludeFeatured: false,
        SortOrder: 'DistanceASC',
      },
    }),

    $rentals('/api/reviews/destination', {
      query: {
        latitude: campground?.Latitude,
        longitude: campground?.Longitude,
        top: 6,
        radiusKm: 1,
      },
    }),

    $rentals('/api/parks', {
      query: {
        CenterLatitude: campground?.Latitude,
        CenterLongitude: campground?.Longitude,
        Country: countryCode,
        PageSize: 4,
        HasImage: true,
        LanguageCode: locale.value,
        RadiusKm: 100,
      },
    }),
  ])

  return { campground, gisData, rvs: nearbyRVs?.PopularRVs?.ListRVs ?? [], nearbyLocations, tripReviews, parks: nearbyParks?.Data ?? [] }
})

if (!data.value?.campground) {
  throw createError({
    status: 404,
    statusText: 'Page Not Found',
    fatal: true,
  })
}

const { prePopulateSearchLocation } = useLocationPredictions()
const { updateSearchFormLocation } = useSearchForm()

onMounted(() => {
  const placeId = data.value?.gisData?.placeId
  const locationText = data.value?.campground?.Name ?? data.value?.gisData?.name ?? ''

  prePopulateSearchLocation(locationText)

  if (placeId) {
    updateSearchFormLocation({
      placeId,
      text: locationText,
    })

    // Campground name is not included in the gis data, so we need to add it manually.
    searchData.value.location = {
      ...data.value?.gisData,
      name: locationText,
    }
  }
})

const rvEarningsQuery = computed(() => ({
  country: data.value?.campground?.CountryCode,
  region: data.value?.campground?.RegionCode,
}))

const { data: rvEarnings } = await useFetch('/api/rv-earnings', {
  query: rvEarningsQuery,
})

const listAnRvEarningsDisplay = computed(() => {
  const max = rvEarnings.value?.max
  const countryCode = data.value?.campground?.CountryCode as COUNTRY_SHORTS | undefined

  if (typeof max !== 'number' || Number.isNaN(max)) {
    return ''
  }

  return priceDisplay({ value: max, countryCode, round: true })
})

const listAnRvAdTitle = computed(() => {
  const amount = listAnRvEarningsDisplay.value

  return amount
    ? t('ads.listAnRV.title', { amount })
    : t('ads.listAnRV.titleFallback')
})

const { formatAsDateMonth } = useDates()

const isOpenYearRound = computed(() => {
  return !data.value?.campground?.BookingSeasonStarts || !data.value?.campground?.BookingSeasonEnds
})

const seasonText = computed(() => {
  if (isOpenYearRound.value) {
    return t('openYearRound')
  }

  return t('openFromTo', {
    start: formatAsDateMonth(datetime(data.value?.campground?.BookingSeasonStarts, 'MMDD')),
    end: formatAsDateMonth(datetime(data.value?.campground?.BookingSeasonEnds, 'MMDD')),
  })
})

const averagePriceText = computed(() => getCampgroundAveragePrice(data.value?.campground?.NightlyRates))

const metaTitle = computed(() => t('metadata.title', { name: data.value?.campground?.Name ?? '', placeName: data.value?.campground?.PlaceName ?? '' }))
const metaDescription = computed(() => t('metadata.description', { name: data.value?.campground?.Name ?? '', placeName: data.value?.campground?.PlaceName ?? '', season: seasonText.value }))

useSeoHeadTags({
  title: metaTitle,
  description: metaDescription,
})

const pageSize = 8
const { queueViewableImpression } = useRvViewableImpressions({
  pageNumber: 1,
  pageSize,
})

const gridRvs = data.value?.rvs?.slice(0, pageSize) ?? []
const gridItemCount = gridRvs.length + 1

const campgroundMosaicImages = computed(() => {
  const campground = data.value?.campground
  const preset = presets.WIDE_LG

  if (!campground?.Photos?.length) {
    return []
  }

  return campground.Photos.map((path) => {
    const url = getDynamicImage({
      path,
      preset,
    }) ?? ''

    return {
      type: 'image' as const,
      path,
      src: url.toString(),
      description: campground.Name ?? '',
      width: preset.width,
      height: preset.height,
    }
  })
})

const hasRelatedPOICity = computed(() => data.value?.nearbyLocations?.some((location) => location.Name === data.value?.campground?.City))

const breadcrumbs = computed(() => {
  const items: Breadcrumb[] = [
    {
      text: t('pageLink.rvRentals'),
      to: localePath({
        name: ROUTE_BASE_NAME.rv_rentals,
      }),
    }]

  const campground = data.value?.campground
  const pathSegments: { text: string, slug: string }[] = []

  if (campground?.Country) {
    pathSegments.push({ text: campground.Country, slug: formatAsSlug(campground.Country) })
  }
  if (campground?.Region) {
    pathSegments.push({ text: campground.Region, slug: formatAsSlug(campground.Region) })
  }
  if (campground?.City && hasRelatedPOICity.value) {
    pathSegments.push({ text: campground.City, slug: formatAsSlug(campground.City) })
  }

  const destinationPath: string[] = []
  for (const segment of pathSegments) {
    destinationPath.push(segment.slug)
    items.push({
      text: segment.text,
      to: localePath({
        name: ROUTE_BASE_NAME.rv_rentals_destination,
        params: { keyword: undefined, destinationPath: [...destinationPath] },
      }),
    })
  }

  items.push({
    text: data.value?.campground?.Name ?? '',
    to: localePath({
      name: ROUTE_BASE_NAME.rv_rentals_campground,
      params: {
        campgroundSlug: campgroundSlug.value,
      },
    }),
  })

  return items
})

function getAmenityLabel(amenity: CampgroundAmenity) {
  if (!AMENITY_LABELS[amenity]) {
    return amenity
  }

  const translationKey = `campgroundAmenities.${AMENITY_LABELS[amenity]}`
  return te(translationKey) ? t(translationKey) : amenity
}

const displayAmenities = computed(() => {
  const amenities = data.value?.campground?.Amenities
  if (!amenities) return []

  return amenities
    .filter((amenity) => AMENITY_LABELS[amenity] != null && AMENITY_ICONS[amenity] != null)
    .map((amenity) => ({
      key: amenity,
      label: getAmenityLabel(amenity),
      icon: AMENITY_ICONS[amenity],
    }))
})

function hasAmenity(amenity: CampgroundAmenity) {
  const amenities = data.value?.campground?.Amenities ?? []
  return amenities.includes(amenity)
}

const electricHookupsTypes = computed(() => {
  const types = []
  if (hasAmenity('Has15AmpHookup')) {
    types.push('15 Amp')
  }
  if (hasAmenity('Has20AmpHookup')) {
    types.push('20 Amp')
  }
  if (hasAmenity('Has30AmpHookup')) {
    types.push('30 Amp')
  }
  if (hasAmenity('Has50AmpHookup')) {
    types.push('50 Amp')
  }
  return types.join(', ')
})

const { defineCampground, defineTouristAttraction } = useAugmentedSchemaOrg()
const absoluteLocalePath = useAbsoluteLocalePath()
const runtimeConfig = useRuntimeConfig()

const BASE_URL = runtimeConfig.public.baseURL
const campground = data.value?.campground
const campgroundId = `#campground-${campground.Id}`
const parks = data.value?.parks?.filter((park) => park?.Name) ?? []

// adapter for Unhead only
function asSchemaOrgNode<T extends object>(node: T) {
  return node as T & Record<string, unknown>
}

useSchemaOrg([
  defineBreadcrumb({
    itemListElement: [
      {
        text: t('pageLink.home'),
        to: localePath({ name: 'index' }),
      },
      ...breadcrumbs.value,
    ].map((breadcrumb) => ({
      name: breadcrumb.text,
      item: breadcrumb.to,
    })),
  }),

  defineWebPage({
    mainEntity: { '@id': `${BASE_URL}/${campgroundId}` },
    mentions: parks.map((_, index) => ({
      '@id': `${BASE_URL}/#park-${index}`,
    })),
  }),

  asSchemaOrgNode(defineCampground({
    id: campgroundId,
    name: campground?.Name ?? '',
    telephone: campground?.PhoneNumber ?? undefined,
    url: campground?.WebsiteLink ?? undefined,
    petsAllowed: hasAmenity('IsPetFriendly'),
    numberOfRooms: campground?.NumberOfSites ?? undefined,
    priceRange: averagePriceText.value?.isFree ? t('common.free') : averagePriceText.value?.text,
    brand: campground?.Brand ?? undefined,
    image: campground?.Photos?.[0] ?? undefined,
    description: campground?.Description ?? undefined,
    latitude: campground?.Latitude,
    longitude: campground?.Longitude,
    streetAddress: campground?.StreetAddress ?? undefined,
    city: campground?.City ?? undefined,
    region: campground?.Region ?? undefined,
    countryCode: campground?.CountryCode ?? undefined,
    postalCode: campground?.PostalCode ?? undefined,
    seasonStarts: campground?.BookingSeasonStarts ?? undefined,
    seasonEnds: campground?.BookingSeasonEnds ?? undefined,
    amenityFeature: campground?.Amenities
      ?.filter((amenity) => AMENITY_LABELS[amenity] && amenity !== 'IsPetFriendly')
      ?.map((amenity) => ({ name: getAmenityLabel(amenity) })),
    reviews: data.value?.tripReviews?.filter((review) => Boolean(review?.Review)).map((review) => ({
      authorName: review?.CreatedByFirstName ?? undefined,
      datePublished: review.CreatedAt,
      reviewBody: review.Review!,
      ratingValue: review?.OverallRating,
    })),
  })),

  ...parks.map((park, index) =>
    asSchemaOrgNode(defineTouristAttraction({
      id: `#park-${index}`,
      name: park.Name!,
      image: park.Images?.[0]?.Path,
      description: park?.Description ?? undefined,
      url: absoluteLocalePath({
        name: ROUTE_BASE_NAME.rv_rentals_park,
        params: { parkSlug: park.Slug },
      }),
      latitude: park.Latitude,
      longitude: park.Longitude,
      city: park?.City ?? undefined,
      region: park?.Region ?? undefined,
      countryCode: park?.Country,
      amenityFeature: park?.RvFacts?.map((fact) => ({ name: fact.Label, value: fact.Value })),
    })),
  ),
])

/**
 * Ads positions
 */
const AD_MIN_POSITION = 2
const AD_MAX_POSITION = computed(() => gridRvs.length > 0 ? gridRvs.length - 1 : 0)

const adPosition = useState(
  'campgroundAdPosition',
  () => randomNumber(AD_MIN_POSITION, AD_MAX_POSITION.value),
)

function rvGridIndex(rvIndex: number) {
  return rvIndex <= adPosition.value ? rvIndex : rvIndex + 1
}

const { logCustomEvent } = await useBraze()
const rtsessionIdCookie = useRtsessionIdCookie()

onMounted(() => {
  const campground = data.value?.campground
  const countryCode = (campground?.CountryCode ?? 'CA') as COUNTRY_SHORTS
  const currency = countryCode ? lookupCountryCodeToCurrencyCode(countryCode) : 'CAD'
  const destinationName = pointOfInterestLocationNameWithPreposition({
    name: campground?.Name,
    component: POINT_OF_INTEREST_TYPE.Campground,
    locale: locale.value,
    useNearby: true,
  })

  // Don't fire the Braze event when earnings data is unavailable or the API returned
  // zero/empty values — sending empty amounts produces misleading in-app messages.
  if (!rvEarnings.value?.min || !rvEarnings.value?.max) return

  logCustomEvent('InApp_CampgroundDestinationPageViewed', {
    destinationName,
    currency,
    minAmount: priceDisplay({ value: rvEarnings.value.min, countryCode, round: true }),
    maxAmount: priceDisplay({ value: rvEarnings.value.max, countryCode, round: true }),
    cta: linkListRv.value,
    language: locale.value,
    sessionId: rtsessionIdCookie.value,
  })
})
</script>

<i18n src="~~/i18n/locales/common/campground.json" lang="json" />

<i18n lang="json">
{
  "en": {
    "metadata": {
      "title": "{name}, {placeName} - RV Camping & Rentals",
      "description": "Plan your RV trip to {name} in {placeName}. {season} - Browse amenities, check availability, and book RV rentals with delivery on RVezy."
    },
    "title": "{name} - RV Camping in {placeName}",
    "subtitle": "Discover the RVezy RV rental marketplace",
    "editCampground": "Edit campground",
    "openYearRound": "Open year-round",
    "openFromTo": "Open from {start} to {end}",
    "near": "near {location}",
    "exploreRVRentals": "Explore all RV rentals {locationName}",
    "getDirections": "Get directions",
    "campgroundInPlace": "Campground in {placeName}",
    "campgroundFeatures": "Campground features",
    "hookups": "Hookups",
    "rvSitesCount": "Sites | 1 Site | {n} sites",
    "campgroundRestrictions": "Campground restrictions",
    "maxRVLength": "Maximum RV length: {length} ft",
    "noMaxRVLength": "No max RV length",
    "motorizedRVsOnly": "Motorized RVs only",
    "allRVsWelcome": "All RVs welcome",
    "ageRestriction": "Age restriction",
    "noAgeRestrictions": "No age restrictions",
    "rentalRVsNear": "Rental RVs near {name}",
    "rvs": "RVs",
    "campgroundTitle": "Campground title",
    "rvRentalsNear": "RV rentals near {name}",
    "ParksToVisitNear": "Parks to visit near {name}",
    "viewDeliverableRVs": "View deliverable RVs",
    "contact": "Contact",
    "phone": "Telephone",
    "website": "Website",
    "bookCampsite": "Book a campsite",

    "ads": {
      "listAnRV": {
        "title": "Do you own an RV? You could earn up to {amount}/year.",
        "titleFallback": "Do you own an RV? Earn extra income on RVezy.",
        "cta": "List your RV"
      }
    }
  },
  "fr": {
    "metadata": {
      "title": "{name}, {placeName} — Camping et location de VRs",
      "description": "Planifiez votre séjour en VR à {name} à {placeName}. {season} - Découvrez les commodités, vérifiez la disponibilité et louez un VR avec livraison sur RVezy."
    },
    "title": "{name} — Camping en VR à {placeName}",
    "subtitle": "Découvrez la plateforme de location de VRs entre particuliers RVezy",
    "editCampground": "Modifier le camping",
    "openYearRound": "Ouvert à l'année",
    "openFromTo": "Ouvert du {start} au {end}",
    "near": "près de {location}",
    "exploreRVRentals": "Explorer les VRs {locationName}",
    "getDirections": "Obtenir un itinéraire",
    "campgroundInPlace": "Camping à {placeName}",
    "campgroundFeatures": "Caractéristiques du camping",
    "hookups": "Branchements",
    "rvSitesCount": "Sites de camping | 1 Site de camping | {n} sites de camping",
    "campgroundRestrictions": "Restrictions du camping",
    "maxRVLength": "Longueur maximum du VR : {length} pi",
    "noMaxRVLength": "Pas de longueur maximum du VR",
    "motorizedRVsOnly": "VRs motorisés uniquement",
    "allRVsWelcome": "Tous les VRs sont acceptés",
    "ageRestriction": "Restriction d'âge",
    "noAgeRestrictions": "Aucune restriction d'âge",
    "rentalRVsNear": "VRs à louer près de {name}",
    "rvs": "VRs",
    "campgroundTitle": "Titre du camping",
    "rvRentalsNear": "Location de VR près de {name}",
    "ParksToVisitNear": "Parcs à visiter près du camping {name}",
    "viewDeliverableRVs": "Voir les VRs livrables",
    "contact": "Contact",
    "phone": "Téléphone",
    "bookCampsite": "Réserver un site",
    "website": "Site web",

    "ads": {
      "listAnRV": {
        "title": "Vous possédez un VR ? Vous pouvez gagner jusqu'à {amount}/an.",
        "titleFallback": "Vous possédez un VR ? Gagnez des revenus supplémentaires sur RVezy.",
        "cta": "Inscrivez votre VR"
      }
    }
  }
}
</i18n>
